import React, { useState, useEffect } from 'react';
import {
  MdArrowBack, MdPerson, MdEmail, MdCheckCircle, MdSend,
  MdVisibility, MdVisibilityOff, MdExpandMore, MdLockOutline,
} from 'react-icons/md';
import accountsData from '../data/accounts.json';
import { useProfile } from '../contexts/ProfileContext';
import { useApp, maskAmount } from '../contexts/AppContext';

// Recent transfers — replaces the old contact-card grid. Tapping a row
// pre-fills the form so the list still doubles as a quick re-send.
const RECENT_TRANSFERS = [
  { name: 'Sarah Chen',   email: 'sarah.chen@email.com', amount: 150.00, date: 'May 2, 2026', status: 'Completed' },
  { name: 'Mike Johnson', email: 'mike.j@gmail.com',     amount: 75.50,  date: 'May 1, 2026', status: 'Completed' },
  { name: 'Emily Park',   email: 'emily.park@work.ca',   amount: 200.00, date: 'Apr 28, 2026', status: 'Completed' },
  { name: 'David Torres', email: 'dtorres@outlook.com',  amount: 45.00,  date: 'Apr 25, 2026', status: 'Completed' },
];

const bankingAccounts = accountsData.bankingData.filter(a => a.currency === 'CAD');

const initials = (name = '') =>
  name.trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase();

// NaN-proof: accepts numbers, dollar-formatted strings, null, undefined, ''.
const fmtMoney = v => {
  const n = typeof v === 'number' && Number.isFinite(v)
    ? v
    : parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  const safe = Number.isFinite(n) ? n : 0;
  return `$${safe.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function getAccounts(profile) {
  if (!profile?.accounts) return bankingAccounts;
  const accts = [];
  if (profile.accounts.chequing) accts.push({
    id: 'chequing',
    name: profile.accounts.chequing.name || 'Chequing Account',
    num: '...' + String(profile.accounts.chequing.account_number || '').slice(-4),
    balance: profile.accounts.chequing.current_balance ?? profile.accounts.chequing.current_balance_owing ?? profile.accounts.chequing.current_value ?? profile.accounts.chequing.current_market_value ?? profile.accounts.chequing.balance ?? 0,
  });
  if (profile.accounts.savings) accts.push({
    id: 'savings',
    name: profile.accounts.savings.name || 'Savings Account',
    num: '...' + String(profile.accounts.savings.account_number || '').slice(-4),
    balance: profile.accounts.savings.current_balance ?? profile.accounts.savings.current_balance_owing ?? profile.accounts.savings.current_value ?? profile.accounts.savings.current_market_value ?? profile.accounts.savings.balance ?? 0,
  });
  if (accts.length) return accts;
  // Fallback to mock data; normalize balance to number and num to last-4 mask.
  return bankingAccounts.map(a => {
    const numStr = String(a.num || '').replace(/\s+/g, '');
    const balanceNum = typeof a.balance === 'number'
      ? a.balance
      : parseFloat(String(a.balance ?? '').replace(/[^0-9.-]/g, '')) || 0;
    return {
      id: a.id,
      name: a.name,
      num: numStr.length >= 4 ? '...' + numStr.slice(-4) : numStr,
      balance: balanceNum,
    };
  });
}

const EmailMoneyTransferScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  // Privacy state lives in AppContext now so flipping the eye on Home
  // applies app-wide — the local hideAmounts/setHide pair was removed.
  const { hideBalances, setHideBalances } = useApp();
  const accounts = getAccounts(loadedProfileData?.profile);

  const [step, setStep]            = useState('form'); // 'form' | 'review' | 'verify' | 'success'
  const [fromAccount, setFrom]     = useState(accounts[0]?.id || 'chequing');
  const [recipientName, setName]   = useState('');
  const [recipientEmail, setEmail] = useState('');
  const [amount, setAmount]        = useState('');
  const [message, setMessage]      = useState('');
  const [errors, setErrors]        = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);

  // Verification step state
  const [verifyCode, setVerifyCode]     = useState('');
  const [verifyError, setVerifyError]   = useState('');
  const [verifySending, setVerifySending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const selectedAccount = accounts.find(a => a.id === fromAccount) || accounts[0];

  // Sender email from the loaded profile, falls back to a friendly placeholder
  // so the verify step always has something to show in dev/demo mode.
  const senderEmail =
    loadedProfileData?.profile?.customer?.email ||
    loadedProfileData?.profile?.email ||
    'you@td-demo.ca';

  // Mask middle of an email for the verify step copy: jl••••@td-demo.ca
  const maskEmail = (em) => {
    if (!em || !em.includes('@')) return em;
    const [local, domain] = em.split('@');
    const visible = local.slice(0, 2);
    return `${visible}${'•'.repeat(Math.max(2, local.length - 2))}@${domain}`;
  };

  // Mask helper — used for any displayed amount. The amount input itself
  // stays unmasked while the user is typing so they can verify entry.
  const showAmount = v => maskAmount(fmtMoney(v), hideBalances);

  const validate = () => {
    const e = {};
    if (!recipientName.trim())                              e.name   = 'Recipient name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) e.email  = 'Enter a valid email';
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0)                  e.amount = 'Enter a valid amount';
    else if (amt > 3000)                                    e.amount = 'Maximum single transfer is $3,000';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => { if (validate()) setStep('review'); };

  // From Review → Verify: simulate sending the code, kick off resend cooldown.
  const handleSend = () => {
    setVerifyCode('');
    setVerifyError('');
    setVerifySending(true);
    setResendCountdown(30);
    setStep('verify');
    // Simulate a small network delay before the "code sent" affordance settles.
    setTimeout(() => setVerifySending(false), 600);
  };

  // Mock code: any 6 digits work; 000000 deliberately fails so we can show the
  // error path in demo. In production this would hit /api/auth/verify-code.
  const handleVerify = () => {
    const code = verifyCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setVerifyError('Enter the 6-digit code from your email.');
      return;
    }
    if (code === '000000') {
      setVerifyError("That code didn't match. Try again or resend.");
      return;
    }
    setStep('success');
  };

  const handleResend = () => {
    if (resendCountdown > 0) return;
    setVerifySending(true);
    setVerifyError('');
    setResendCountdown(30);
    setTimeout(() => setVerifySending(false), 600);
  };

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setInterval(() => setResendCountdown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCountdown]);

  const reset = () => {
    setStep('form');
    setAmount(''); setName(''); setEmail(''); setMessage(''); setErrors({});
    setVerifyCode(''); setVerifyError(''); setResendCountdown(0);
  };

  const pickRecent = (t) => {
    setName(t.name);
    setEmail(t.email);
    setAmount(String(t.amount));
    setErrors({});
  };

  // Privacy toggle in the hero — clicking flips the GLOBAL hideBalances
  // flag (AppContext), so toggling here is the same as toggling the eye
  // in the home top bar and stays in sync across screens.
  const PrivacyToggle = () => (
    <button
      type="button"
      className="bh-flow-back"
      onClick={() => setHideBalances(v => !v)}
      aria-label={hideBalances ? 'Show amounts' : 'Hide amounts'}
      aria-pressed={hideBalances}
    >
      {hideBalances ? <MdVisibilityOff /> : <MdVisibility />}
    </button>
  );

  // ── Success ────────────────────────────────────────────────
  if (step === 'success') return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Interac e-Transfer</h1>
        </div>
        <PrivacyToggle />
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-md)' }}>
          <div className="bh-success-icon">
            <MdCheckCircle size={56} />
          </div>
          <h2 className="bh-success-title">Transfer Sent!</h2>
          <p className="bh-amount-display" style={{ margin: 0 }}>{showAmount(amount)}</p>
          <p className="bh-success-subtitle">
            sent to <strong style={{ color: 'var(--bh-black)' }}>{recipientName}</strong>
          </p>
          <p className="bh-success-subtitle" style={{ fontSize: '0.875rem' }}>{recipientEmail}</p>

          <div className="bh-info-box" style={{ width: '100%' }}>
            The recipient will receive an email notification and can deposit the funds within 30 days.
          </div>

          <button className="bh-btn-cta" onClick={reset}>Send Another</button>
          <button className="bh-btn-outline-dark" style={{ width: '100%' }} onClick={() => onNavigate('home')}>Done</button>
        </div>
      </div>
    </div>
  );

  // ── Review ─────────────────────────────────────────────────
  if (step === 'review') return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={() => setStep('form')} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Review Transfer</h1>
        </div>
        <PrivacyToggle />
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ alignItems: 'center' }}>
          <div className="bh-avatar size-xl">{initials(recipientName)}</div>
          <p style={{ margin: 0, font: 'var(--bh-h3)', color: 'var(--bh-black)' }}>{recipientName}</p>
          <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>{recipientEmail}</p>
          <p className="bh-amount-display" style={{ marginTop: 'var(--space-2xs)' }}>{showAmount(amount)}</p>

          <div className="bh-review-rows" style={{ width: '100%' }}>
            <div className="bh-review-row">
              <span className="bh-review-row-label">From</span>
              <span className="bh-review-row-value">
                {selectedAccount?.name}<br />
                <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                  {selectedAccount?.num} · {showAmount(selectedAccount?.balance)}
                </span>
              </span>
            </div>
            <div className="bh-review-row">
              <span className="bh-review-row-label">Amount</span>
              <span className="bh-review-row-value">{showAmount(amount)} CAD</span>
            </div>
            {message && (
              <div className="bh-review-row">
                <span className="bh-review-row-label">Message</span>
                <span className="bh-review-row-value" style={{ fontStyle: 'italic' }}>"{message}"</span>
              </div>
            )}
            <div className="bh-review-row">
              <span className="bh-review-row-label">Delivery</span>
              <span className="bh-review-row-value">Interac e-Transfer · ~30 min</span>
            </div>
          </div>

          <div className="bh-info-box" style={{ width: '100%' }}>
            By sending this transfer you agree to TD's Interac e-Transfer terms. The recipient will receive an email notification.
          </div>

          <div className="bh-btn-row" style={{ width: '100%' }}>
            <button className="bh-btn-outline-dark" onClick={() => setStep('form')}>Back</button>
            <button className="bh-btn-dark" onClick={handleSend}>
              <MdSend size={16} /> Send Transfer
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Verify ─────────────────────────────────────────────────
  // Email-code verification gate before the transfer actually goes out.
  // Mock: any 6-digit code passes; '000000' fails so we can demo the error.
  if (step === 'verify') return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={() => setStep('review')} aria-label="Back to review">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Verify Transfer</h1>
        </div>
        <PrivacyToggle />
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card" style={{ alignItems: 'center', gap: 'var(--space-md)' }}>
          <div className="bh-success-icon" style={{ background: 'var(--bh-light-gray)', color: 'var(--bh-black)' }}>
            <MdLockOutline size={40} />
          </div>
          <h2 className="bh-success-title">Check your email</h2>
          <p className="bh-success-subtitle" style={{ maxWidth: 320 }}>
            We sent a 6-digit code to <strong style={{ color: 'var(--bh-black)' }}>{maskEmail(senderEmail)}</strong>.
            Enter it below to confirm sending {showAmount(amount)} to {recipientName}.
          </p>

          <div className="bh-field" style={{ width: '100%', maxWidth: 280 }}>
            <label htmlFor="emt-verify-code" className="bh-field-label" style={{ justifyContent: 'center' }}>
              Verification code
            </label>
            <input
              id="emt-verify-code"
              className={`bh-field-input${verifyError ? ' is-error' : ''}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="••••••"
              value={verifyCode}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setVerifyCode(v);
                if (verifyError) setVerifyError('');
              }}
              onKeyDown={e => { if (e.key === 'Enter' && verifyCode.length === 6) handleVerify(); }}
              style={{ textAlign: 'center', letterSpacing: '0.5em', font: 'var(--bh-h2)' }}
            />
            {verifyError && <div className="bh-field-error" style={{ textAlign: 'center' }}>{verifyError}</div>}
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendCountdown > 0 || verifySending}
            style={{
              background: 'transparent', border: 'none',
              color: resendCountdown > 0 ? 'var(--bh-gray)' : 'var(--td-green)',
              font: 'var(--bh-body-sm)', fontWeight: 600,
              cursor: resendCountdown > 0 ? 'default' : 'pointer',
              padding: 'var(--space-2xs)',
            }}
          >
            {verifySending ? 'Sending…'
              : resendCountdown > 0 ? `Resend code in ${resendCountdown}s`
              : 'Resend code'}
          </button>

          <div className="bh-info-box" style={{ width: '100%' }}>
            For your security, the code expires in 5 minutes. We never call or
            email asking you to read this code aloud.
          </div>

          <div className="bh-btn-row" style={{ width: '100%' }}>
            <button className="bh-btn-outline-dark" onClick={() => setStep('review')}>Back</button>
            <button
              className="bh-btn-cta"
              onClick={handleVerify}
              disabled={verifyCode.length !== 6 || verifySending}
              style={{ flex: 1 }}
            >
              <MdSend size={16} /> Confirm &amp; Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Form ───────────────────────────────────────────────────
  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Interac e-Transfer</h1>
        </div>
        <PrivacyToggle />
      </header>

      <div className="bh-flow-body">
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">Send Money</h2>

          {/* From Account — 2-line picker so name/num/balance fit on small screens */}
          <div className="bh-field">
            <span id="emt-from-label" className="bh-field-label">From Account</span>
            <div className="bh-acct-picker" data-open={pickerOpen}>
              <button
                type="button"
                className="bh-acct-row"
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
                aria-labelledby="emt-from-label"
                onClick={() => setPickerOpen(o => !o)}
              >
                <div className="bh-acct-row-info">
                  <div className="bh-acct-row-name">{selectedAccount?.name || 'Select account'}</div>
                  <div className="bh-acct-row-meta">
                    <span>{selectedAccount?.num}</span>
                    <span>{showAmount(selectedAccount?.balance)}</span>
                  </div>
                </div>
                <MdExpandMore size={22} className="bh-acct-chevron" />
              </button>
              {pickerOpen && accounts.length > 1 && (
                <div className="bh-acct-options" role="listbox">
                  {accounts.filter(a => a.id !== fromAccount).map(a => (
                    <button
                      key={a.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="bh-acct-row"
                      onClick={() => { setFrom(a.id); setPickerOpen(false); }}
                    >
                      <div className="bh-acct-row-info">
                        <div className="bh-acct-row-name">{a.name}</div>
                        <div className="bh-acct-row-meta">
                          <span>{a.num}</span>
                          <span>{showAmount(a.balance)}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recipient Name */}
          <div className="bh-field">
            <label htmlFor="emt-name" className="bh-field-label">
              <MdPerson size={14} /> Recipient Name
            </label>
            <input
              id="emt-name"
              className={`bh-field-input${errors.name ? ' is-error' : ''}`}
              placeholder="Recipient's full name"
              value={recipientName}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
            />
            {errors.name && <div className="bh-field-error">{errors.name}</div>}
          </div>

          {/* Recipient Email */}
          <div className="bh-field">
            <label htmlFor="emt-email" className="bh-field-label">
              <MdEmail size={14} /> Email Address
            </label>
            <input
              id="emt-email"
              type="email"
              className={`bh-field-input${errors.email ? ' is-error' : ''}`}
              placeholder="recipient@email.com"
              value={recipientEmail}
              onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }}
            />
            {errors.email && <div className="bh-field-error">{errors.email}</div>}
          </div>

          {/* Amount — input stays visible while typing regardless of privacy */}
          <div className="bh-field">
            <label htmlFor="emt-amount" className="bh-field-label">Amount</label>
            <input
              id="emt-amount"
              type="number"
              min="0"
              step="0.01"
              className={`bh-field-input is-amount${errors.amount ? ' is-error' : ''}`}
              placeholder="$0.00"
              value={amount}
              onChange={e => { setAmount(e.target.value); setErrors(p => ({ ...p, amount: '' })); }}
            />
            {errors.amount && <div className="bh-field-error">{errors.amount}</div>}
            <div className="bh-quick-amounts">
              {['25', '50', '100', '200'].map(v => (
                <button
                  key={v}
                  type="button"
                  className={`bh-quick-amount${amount === v ? ' selected' : ''}`}
                  onClick={() => { setAmount(v); setErrors(p => ({ ...p, amount: '' })); }}
                >
                  ${v}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className="bh-field">
            <label htmlFor="emt-msg" className="bh-field-label">
              Message <span style={{ fontWeight: 400, color: 'var(--bh-gray)' }}>(optional)</span>
            </label>
            <textarea
              id="emt-msg"
              className="bh-field-textarea"
              placeholder="What's this for?"
              maxLength={100}
              rows={2}
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </div>

          <button className="bh-btn-cta" onClick={handleContinue}>Review Transfer</button>
        </div>

        {/* Recent Transfers — list with name/date and amount/status */}
        <section>
          <h3 className="bh-section-title" style={{ paddingLeft: 0 }}>Recent Transfers</h3>
          <div className="bh-recent-list">
            {RECENT_TRANSFERS.map(t => (
              <button
                key={t.email + t.date}
                type="button"
                className="bh-recent-row"
                onClick={() => pickRecent(t)}
              >
                <div className="bh-recent-row-info">
                  <span className="bh-recent-row-name">{t.name}</span>
                  <span className="bh-recent-row-date">{t.date}</span>
                </div>
                <div className="bh-recent-row-meta">
                  <span className="bh-recent-row-amount">{showAmount(t.amount)}</span>
                  <span className="bh-recent-row-status">{t.status}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default EmailMoneyTransferScreen;
